// Pages get breadcrumbs via the shells' `crumbs` prop; embedded surfaces can use this directly.
import { type ComponentPropsWithoutRef, type FC, forwardRef } from "react";
import { Breadcrumbs as UIBreadcrumbs } from "@hightouch/htui";

import { RouterLink, useLocation } from "src/router";
import { resolveBreadcrumbIcon } from "src/components/layout/breadcrumb-icon-map";
import type { Crumb } from "src/components/layout/types";

type BreadcrumbsProps = {
  crumbs: Crumb[];
  /** Prefix the first crumb with the page's route icon. @default false */
  showIcon?: boolean;
  /** Opens each crumb in a new tab, for a view over a page that a link would otherwise leave covering it. */
  openInNewTab?: boolean;
};

const BreadcrumbLink = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<"a">
>(({ href = "", ...props }, ref) => (
  <RouterLink ref={ref} to={href} {...props} />
));
BreadcrumbLink.displayName = "BreadcrumbLink";

const NewTabBreadcrumbLink = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<"a">
>(({ href = "", ...props }, ref) => (
  <RouterLink
    ref={ref}
    to={href}
    target="_blank"
    rel="noopener noreferrer"
    {...props}
  />
));
NewTabBreadcrumbLink.displayName = "NewTabBreadcrumbLink";

export const Breadcrumbs: FC<BreadcrumbsProps> = ({
  crumbs,
  showIcon = false,
  openInNewTab = false,
}) => {
  const location = useLocation();
  const pageIcon = showIcon
    ? resolveBreadcrumbIcon(location.pathname)
    : undefined;

  return (
    <UIBreadcrumbs
      linkComponent={openInNewTab ? NewTabBreadcrumbLink : BreadcrumbLink}
      items={crumbs.map(({ link, icon, ...crumb }, index) => ({
        ...crumb,
        href: link,
        icon: icon ?? (index === 0 ? pageIcon : undefined),
      }))}
    />
  );
};
